import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Overlay from '../Overlay';
import {
  formatsForSubject,
  getSubject,
  getFormat,
  destinationBlockedReason,
  labelDetailText,
  DESTINATIONS,
} from '../../lib/labelFormats';
import {
  fetchBranchPrinterOptions,
  printInventoryBatch,
  printToolBatch,
  buildInventoryZpl,
  buildToolZpl,
  dispatchZebraBatch,
  subscribeToPrintBatch,
  cancelPrintBatch,
  retryPrintBatchErrors,
  LABEL_PROFILES,
} from '../../utils/printLabel';
import { exportCanonM5PDF, computeM5Layout } from '../../utils/canonM5Export';

// One modal, four modules — Session 75, built to
// docs/gates/Stede_LabelPrinting_Gate_Phase4.html.
//
// Replaces the two hardcoded batch buttons on FleetPage and InventoryLabelPrint,
// which each printed to one specific Brother and walked straight past the
// printer registry. Session 76 swaps those buttons over.
//
// The order of the three controls is a decision, not a layout: SIZE comes
// before DESTINATION because size eliminates destinations (callout B). A 3x5
// cannot go on 24mm tape, so asking for the printer first means offering
// choices the next answer invalidates.

const ACCENT_FALLBACK = '#1D9E75';

// Which HTML TEMPLATE a rung uses on the browser-print path. Not a page size -
// the page size comes from the ladder now, via formatStyles(). This map only
// answers "which layout", and the tool rung is the one that differs.
//
// Session 76: renamed from FORMAT_TO_BROTHER_PROFILE. The destination is no
// longer "a Brother" - it is whatever printer the operating system is pointed
// at - so keying the name on one manufacturer had become misleading.
const FORMAT_TO_TEMPLATE_PROFILE = {
  '2x1': 'pt_e560bt_2x1',
  '3x1': 'pt_e560bt_2x1',
  'tool_1_5x1': 'pt_e560bt_tool',
};

// Rough throughput for the estimate on the summary bar. Deliberately coarse —
// it exists to distinguish "a moment" from "go and do something else", not to
// be accurate to the second.
const SECONDS_PER_LABEL = { local: 4, zebra: 2, canon: 0 };

// Raw failure codes are not messages. `popup_blocked` reached the user as the
// literal string "popup_blocked" in a red box - Colby saw it on his first test
// of the local destination. The old page-level handler had proper copy for
// exactly this case and it was lost when errors moved into the modal.
const ERROR_COPY = {
  // #339, Session 77. `popup_blocked` is gone with the pop-up - the browser
  // print path is a hidden same-document iframe now, which needs no permission.
  // `print_frame_failed` replaces it and means something much rarer: the page
  // could not attach a frame at all.
  print_frame_failed: 'Could not open the print view. Reload the page and try again.',
  no_items: 'Nothing selected to print.',
  no_tools: 'Nothing selected to print.',
  invalid_profile: 'That label size is not available for this destination.',
  invalid_subject: 'That label size is not available for this destination.',
  layout_failed: 'Could not lay this batch out on the roll. Try a different size.',
  pdf_init_failed: 'Could not start the PDF. Reload the page and try again.',
  pdf_save_failed: 'The PDF was built but could not be saved. Check your downloads.',
};

function errorText(code) {
  return ERROR_COPY[code] || code || 'Print failed. Please try again.';
}

function estimateText(destinationId, labelCount) {
  if (destinationId === 'canon') return 'Instant';
  const seconds = labelCount * (SECONDS_PER_LABEL[destinationId] || 2);
  if (seconds < 60) return 'Under a minute';
  return `Est. ${Math.round(seconds / 60)} min`;
}

// Gate item D10, pulled forward at Session 76.
//
// The gate shipped copies as one stepper for the whole batch and listed
// per-item copies as out of scope — but InventoryLabelPrint.js ALREADY had a
// per-item copies box on every selected row, so wiring that page to a
// uniform-only modal would have removed a capability it shipped with. The gate
// recorded D10 as a feature not yet built; on that one screen it was.
//
// `initialCopies` is an { [itemId]: number } map. Uncontrolled by design: the
// modal owns the quantities once it opens, and reseeds from this prop on every
// open. Absent or empty, everything below behaves exactly as Session 75's
// uniform stepper did.
function seedCopies(items, initialCopies) {
  const seeded = {};
  items.forEach(item => {
    const n = parseInt(initialCopies?.[item.id], 10);
    seeded[item.id] = Number.isFinite(n) ? Math.min(99, Math.max(1, n)) : 1;
  });
  return seeded;
}

export default function BatchPrintModal({
  open,
  onClose,
  subjectType = 'inventory',
  items = [],
  initialCopies = null,
  branchName,
  userId,
  onComplete,
}) {
  const subject = getSubject(subjectType) || getSubject('inventory');
  const accent = subject?.accent || ACCENT_FALLBACK;

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 768); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const availableFormats = useMemo(
    () => formatsForSubject(subject.id, { mobile: isMobile }),
    [subject.id, isMobile]
  );

  const [formatId, setFormatId] = useState(subject.defaultFormat);
  // `copies` is the SET-ALL value the stepper shows. `copiesById` is what
  // actually prints. They agree until someone edits a single row.
  const [copies, setCopies] = useState(1);
  const [copiesById, setCopiesById] = useState(() => seedCopies(items, initialCopies));
  const [showPerItem, setShowPerItem] = useState(false);
  const [destinationId, setDestinationId] = useState(null);
  const [printers, setPrinters] = useState({ zebraPrinters: [] });
  const [printersLoaded, setPrintersLoaded] = useState(false);
  const [zebraPrinterId, setZebraPrinterId] = useState(null);
  const [phase, setPhase] = useState('configure'); // configure | working | live | done
  // #113: the finished run's own report, kept so the done banner can name what
  // went wrong rather than only that something finished.
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [batch, setBatch] = useState(null); // { batchId, jobs }

  // The 24x30 wall label is desktop-only (callout K). If the viewport narrows
  // while it is selected, fall back rather than leaving an invisible choice
  // active — that would print something the user can no longer see selected.
  useEffect(() => {
    if (!availableFormats.some(f => f.id === formatId)) {
      setFormatId(subject.defaultFormat);
    }
  }, [availableFormats, formatId, subject.defaultFormat]);

  useEffect(() => {
    if (!open || !branchName) return;
    let cancelled = false;
    setPrintersLoaded(false);
    fetchBranchPrinterOptions(branchName).then(result => {
      if (!cancelled) {
        setPrinters(result);
        setZebraPrinterId(result.receiveDefaultPrinter?.id || result.zebraPrinters[0]?.id || null);
        setPrintersLoaded(true);
      }
    }).catch(() => {
      // A lookup failure must not leave the modal permanently unselectable —
      // Canon needs no printer registry and stays reachable.
      if (!cancelled) setPrintersLoaded(true);
    });
    return () => { cancelled = true; };
  }, [open, branchName]);

  // Reset when reopened, so a previous run's result never greets the next one.
  //
  // The quantities reseed from the caller here rather than being carried over:
  // the selection can have changed between opens, and a stale count keyed on an
  // id no longer selected would be invisible and would still print.
  //
  // Guarded on the RISING EDGE of `open`, and that guard is load-bearing rather
  // than an optimisation. `items` is built inline by both call sites, so it is
  // a new array identity on every parent render; without the guard this effect
  // would re-run constantly and reset the chosen size and every quantity
  // underneath the user while they were still choosing. The dependency list
  // stays complete — the ref decides whether the body does anything — so no
  // lint suppression is needed, which matters: this project has no ESLint
  // config and an eslint-disable for a React rule is a fatal build error.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    const seeded = seedCopies(items, initialCopies);
    const values = Object.values(seeded);
    const uniform = values.length > 0 && values.every(v => v === values[0]);

    setPhase('configure');
    setError(null);
    setBatch(null);
    setCopiesById(seeded);
    setCopies(uniform ? values[0] : 1);
    setShowPerItem(false);
    setFormatId(subject.defaultFormat);
  }, [open, subject.defaultFormat, items, initialCopies]);

  const format = getFormat(formatId);

  const copiesFor = useCallback(
    item => Math.max(1, copiesById[item.id] ?? 1),
    [copiesById]
  );
  const totalLabels = useMemo(
    () => items.reduce((sum, item) => sum + Math.max(1, copiesById[item.id] ?? 1), 0),
    [items, copiesById]
  );
  // Whether the stepper's single number still describes the whole batch. When
  // it does not, saying "24 items x 1" would be a lie, so the caption changes.
  const copiesVary = useMemo(() => {
    const values = items.map(item => Math.max(1, copiesById[item.id] ?? 1));
    return values.some(v => v !== values[0]);
  }, [items, copiesById]);

  // The value the stepper SHOWS, which is not always the value it last SET.
  //
  // Editing rows individually can land them back on a common number - set both
  // of two rows to 2 and the batch is uniform again, but `copies` is still
  // whatever the stepper was left at. Found by Colby at Session 76 the moment
  // the per-item boxes became usable: the caption read "2 items x 1 = 4 labels",
  // an equation that is not true in any arithmetic. Read the rows, not the
  // stepper's own history.
  const uniformCopies = useMemo(() => {
    if (copiesVary || items.length === 0) return null;
    return Math.max(1, copiesById[items[0].id] ?? 1);
  }, [copiesVary, items, copiesById]);
  const stepperValue = uniformCopies ?? copies;

  // The stepper sets every row. That is the fast path and it stays one tap —
  // per-item edits are a deliberate detour, not the default.
  const setAllCopies = useCallback(value => {
    const n = Math.min(99, Math.max(1, value));
    setCopies(n);
    setCopiesById(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => { next[id] = n; });
      return next;
    });
  }, []);

  const setItemCopies = useCallback((itemId, value) => {
    const n = Math.min(99, Math.max(1, parseInt(value, 10) || 1));
    setCopiesById(prev => ({ ...prev, [itemId]: n }));
  }, []);

  // `local` needs nothing from the registry - every browser has a print dialog.
  // Only the Zebra row's availability is a real lookup.
  const availability = useMemo(() => ({
    zebraCount: printers.zebraPrinters.length,
  }), [printers]);

  const destinationRows = useMemo(() => (
    ['local', 'zebra', 'canon'].map(id => ({
      ...DESTINATIONS[id],
      blockedReason: destinationBlockedReason(formatId, id, availability),
    }))
  ), [formatId, availability]);

  // Size eliminates destinations, so the current pick can become invalid when
  // size changes. Move to the best available rather than silently printing to
  // something the user did not choose.
  //
  // Waits for printersLoaded, and this is load-bearing rather than tidiness.
  // Found by rendering it at Session 75: while fetchBranchPrinterOptions was
  // still in flight both hardware destinations looked blocked, so the only
  // unblocked row was Canon. It got selected, and when the Brother arrived a
  // moment later the pick was still *valid*, so nothing moved. A branch with a
  // working printer defaulted to "Download PDF" — a file when you meant paper.
  //
  // Session 76 did not retire this guard, it made it matter more. `local` is
  // now ALWAYS unblocked, so it plays the role Canon played: without the guard,
  // inventory would settle on the print dialog while the Zebra lookup was in
  // flight, and settle there permanently because that pick stays valid.
  //
  // Preference order mirrors buildPrinterOptionList's existing rules: tools go
  // to the local printer first, inventory to Zebra first. Canon is last in both
  // because it delivers a download rather than a label.
  useEffect(() => {
    if (!printersLoaded) return;
    const stillOk = destinationRows.find(d => d.id === destinationId && !d.blockedReason);
    if (stillOk) return;
    const preference = subject.id === 'tool'
      ? ['local', 'zebra', 'canon']
      : ['zebra', 'local', 'canon'];
    const best = preference
      .map(id => destinationRows.find(d => d.id === id))
      .find(d => d && !d.blockedReason);
    setDestinationId(best ? best.id : null);
  }, [destinationRows, destinationId, printersLoaded, subject.id]);

  const canonLayout = useMemo(
    () => (destinationId === 'canon' ? computeM5Layout(formatId, totalLabels) : null),
    [destinationId, formatId, totalLabels]
  );

  // Live Zebra batch progress — screen 3.
  //
  // subscribeToPrintBatch returns the Supabase channel, NOT an unsubscribe
  // function. Returning it directly from useEffect throws on unmount, because
  // React calls whatever the effect returns.
  // The job list is read once, at subscribe time, to seed the channel's own
  // state map. Held in a ref rather than listed as a dependency: depending on
  // batch.jobs would tear down and resubscribe on every realtime update, which
  // is the update it just received.
  const batchId = batch?.batchId;
  const initialJobsRef = useRef([]);
  useEffect(() => { initialJobsRef.current = batch?.jobs || []; }, [batch]);

  useEffect(() => {
    if (phase !== 'live' || !batchId) return undefined;
    const channel = subscribeToPrintBatch(batchId, initialJobsRef.current, updated => {
      setBatch(b => (b ? { ...b, jobs: updated } : b));
    });
    return () => { channel?.unsubscribe?.(); };
  }, [phase, batchId]);

  // print_jobs.status is a CHECK constraint over exactly these five values —
  // 'pending','sent','done','error','canceled'. The gate's screen 3 labels them
  // done / sent / queued / failed, which are display words, not statuses.
  const counts = useMemo(() => {
    const jobs = batch?.jobs || [];
    return {
      done: jobs.filter(j => j.status === 'done').length,
      sent: jobs.filter(j => j.status === 'sent').length,
      queued: jobs.filter(j => j.status === 'pending').length,
      failed: jobs.filter(j => j.status === 'error').length,
      canceled: jobs.filter(j => j.status === 'canceled').length,
    };
  }, [batch]);

  const handlePrint = useCallback(async () => {
    setError(null);
    setPhase('working');

    // Every destination reports completion in the same shape, enriched with the
    // three things the caller cannot otherwise know — where it went, at what
    // size, and how many of each. FleetPage writes a tool_history row per tool
    // from this, and a row that says "PT-E560BT 2x1" when the batch went to a
    // Zebra as a 3x1 is worse than no row at all.
    // #339, Session 77. The synchronously-opened blank window that used to be
    // created here is gone. It existed only to keep window.open() on the click's
    // call stack; a hidden iframe is not subject to that rule, so there is
    // nothing to open early and nothing to thread through two call layers.
    const completion = result => ({
      ...result,
      destinationId,
      formatId,
      totalLabels,
      copiesById,
    });

    try {
      if (destinationId === 'canon') {
        // #113, Session 77. The Canon path is the one where a failed QR is
        // permanent - it is laminated onto vinyl - so its result is kept rather
        // than discarded after onComplete.
        // A tile is a physical rectangle, so copies expand to entries here.
        // subjectType decides the QR route: without it a tool batch would tile
        // /inventory/scan/:id onto vinyl, which is #320 all over again.
        const result = await exportCanonM5PDF(
          items.flatMap(item => Array(copiesFor(item)).fill(item)),
          branchName,
          formatId,
          { subjectType: subject.id }
        );
        if (!result.success) throw new Error(result.error || 'Export failed');
        setResult(result);
        setPhase('done');
        onComplete?.(completion(result));
        return;
      }

      if (destinationId === 'local') {
        const profileId = FORMAT_TO_TEMPLATE_PROFILE[formatId];
        const result = subject.id === 'tool'
          ? await printToolBatch(items, branchName, profileId, copies, copiesById)
          : await printInventoryBatch(items.map(item => ({ item, copies: copiesFor(item) })), branchName, formatId);
        if (!result.success) throw new Error(result.error || 'Print failed');
        setResult(result);
        setPhase('done');
        onComplete?.(completion(result));
        return;
      }

      // Zebra
      const printer = printers.zebraPrinters.find(p => p.id === zebraPrinterId);
      if (!printer) throw new Error('No printer selected');
      const profile = LABEL_PROFILES[formatId === '3x1' ? 'zebra_3x1' : 'zebra_2x1'];
      // print_jobs.copies carries the count per row, and its CHECK is copies > 0
      // — copiesFor floors at 1, so a cleared box cannot violate it.
      const jobs = items.map(item => ({
        itemId: item.id,
        copies: copiesFor(item),
        zplPayload: subject.id === 'tool'
          ? buildToolZpl(item, branchName, profile)
          : buildInventoryZpl(item, branchName, profile),
      }));
      const result = await dispatchZebraBatch({
        printer, userId, itemType: subject.id, jobs,
      });
      if (!result.success) throw new Error(result.error || 'Dispatch failed');
      setBatch({ batchId: result.batchId, jobs: result.jobs });
      setPhase('live');
      // The dispatch IS the completion for this path. print_jobs owns what
      // happens next, per job; without this the Zebra route was the one
      // destination that left no record on the calling page at all.
      onComplete?.(completion(result));
    } catch (e) {
      setError(errorText(e.message || String(e)));
      setPhase('configure');
    }
  }, [destinationId, formatId, copies, copiesById, copiesFor, items, branchName, subject.id, printers, zebraPrinterId, userId, onComplete]);

  if (!open) return null;

  const destination = destinationId ? DESTINATIONS[destinationId] : null;
  const actionLabel = destinationId === 'canon'
    ? 'Download PDF'                       // D7 / callout F — the honest verb
    : `Print ${totalLabels} label${totalLabels === 1 ? '' : 's'}`;

  return (
    <Overlay variant={isMobile ? 'sheet' : 'modal'} onClose={onClose} maxWidth={520}>
      {phase === 'live'
        ? <LiveBatch
            counts={counts}
            total={batch?.jobs?.length || 0}
            printerName={printers.zebraPrinters.find(p => p.id === zebraPrinterId)?.name}
            branchName={branchName}
            accent={accent}
            onCancelRemaining={async () => { await cancelPrintBatch(batch.batchId); }}
            onRetryFailed={async () => { await retryPrintBatchErrors(batch.batchId); }}
            onClose={onClose}
          />
        : <Configure
            accent={accent}
            isMobile={isMobile}
            subject={subject}
            items={items}
            branchName={branchName}
            formats={availableFormats}
            formatId={formatId}
            setFormatId={setFormatId}
            format={format}
            copies={stepperValue}
            setCopies={setAllCopies}
            copiesById={copiesById}
            setItemCopies={setItemCopies}
            copiesVary={copiesVary}
            showPerItem={showPerItem}
            setShowPerItem={setShowPerItem}
            destinationRows={destinationRows}
            destinationId={destinationId}
            setDestinationId={setDestinationId}
            zebraPrinters={printers.zebraPrinters}
            zebraPrinterId={zebraPrinterId}
            setZebraPrinterId={setZebraPrinterId}
            canonLayout={canonLayout}
            totalLabels={totalLabels}
            estimate={estimateText(destinationId, totalLabels)}
            actionLabel={actionLabel}
            busy={phase === 'working'}
            done={phase === 'done'}
            result={result}
            destination={destination}
            error={error}
            onPrint={handlePrint}
            onClose={onClose}
          />
      }
    </Overlay>
  );
}

// ---------------------------------------------------------------------------
// Screens 1, 2 and 4 — configure
// ---------------------------------------------------------------------------

function Configure({
  accent, isMobile, subject, items, branchName, formats, formatId, setFormatId, format,
  copies, setCopies, copiesById, setItemCopies, copiesVary, showPerItem, setShowPerItem,
  destinationRows, destinationId, setDestinationId,
  zebraPrinters, zebraPrinterId, setZebraPrinterId, canonLayout, totalLabels,
  estimate, actionLabel, busy, done, result, destination, error, onPrint, onClose,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxHeight: isMobile ? '90vh' : '85vh' }}>
      <Header
        title="Print labels"
        subtitle={`${items.length} item${items.length === 1 ? '' : 's'} selected · ${branchName || '—'}`}
        onClose={onClose}
      />

      {/* Callout I: on mobile the action bar sits OUTSIDE this scroll region. */}
      <div style={{ overflowY: 'auto', padding: isMobile ? '14px 16px' : '18px 22px', flex: 1 }}>
        <Section label="Label size">
          {/* Callout J: horizontal scroller on mobile rather than wrapping to
              three rows, which would push the destination list below the fold. */}
          <div style={{
            display: 'flex',
            gap: 8,
            flexWrap: isMobile ? 'nowrap' : 'wrap',
            overflowX: isMobile ? 'auto' : 'visible',
            paddingBottom: isMobile ? 4 : 0,
            WebkitOverflowScrolling: 'touch',
          }}>
            {formats.map(f => (
              <SizeChip
                key={f.id}
                format={f}
                selected={f.id === formatId}
                accent={accent}
                compact={isMobile}
                onSelect={() => setFormatId(f.id)}
              />
            ))}
          </div>
          {/* Callout A: the distance is the warning, so it lives on the control. */}
          {format?.blurb && (
            <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 10, lineHeight: 1.45 }}>
              {format.blurb}
            </div>
          )}
        </Section>

        <Section label="Copies of each">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Stepper value={copies} onChange={setCopies} accent={accent} />
            {/* D10. Only worth offering when there is more than one row to
                differ, and collapsed by default — the fast path stays a single
                stepper and one tap. */}
            {items.length > 1 && (
              <button
                onClick={() => setShowPerItem(v => !v)}
                aria-expanded={showPerItem}
                style={{
                  background: 'none', border: 'none', padding: '8px 2px', minHeight: 44,
                  color: accent, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >
                {showPerItem ? 'Hide per item' : 'Set per item'}
              </button>
            )}
          </div>

          {/* Callout D: state the multiplication, so nobody discovers they asked
              for 144 stickers after the roll has run. When the counts differ per
              row the multiplication no longer describes the batch, so the line
              states the true total instead of a product that is not true. */}
          <div style={{ fontSize: 12.5, color: '#6B7280', marginTop: 8 }}>
            {copiesVary ? (
              <>
                Counts differ by item — <strong>{totalLabels} label{totalLabels === 1 ? '' : 's'}</strong> across {items.length} item{items.length === 1 ? '' : 's'}. The stepper sets them all to {copies}.
              </>
            ) : (
              <>
                Every selected item gets this many. {items.length} item{items.length === 1 ? '' : 's'} × {copies} = <strong>{totalLabels} label{totalLabels === 1 ? '' : 's'}</strong>.
              </>
            )}
          </div>

          {showPerItem && (
            <div style={{
              marginTop: 12, border: '1px solid #EDEFF2', borderRadius: 10,
              maxHeight: 220, overflowY: 'auto',
            }}>
              {items.map((item, i) => (
                <div
                  key={item.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px',
                    borderTop: i === 0 ? 'none' : '1px solid #F2F4F6',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600, color: '#1a1a1a',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                      {item.name || '—'}
                    </div>
                    <div style={{ fontSize: 11, color: '#8B94A1' }}>
                      {labelDetailText(subject.id, item)}
                    </div>
                  </div>
                  {/* 16px minimum — global rule, iOS auto-zoom. */}
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={copiesById[item.id] ?? 1}
                    onChange={e => setItemCopies(item.id, e.target.value)}
                    // Select on focus, matching InventoryActionCatalog's cart
                    // quantity inputs. Without it, clicking a box showing 1 and
                    // typing 2 yields 12 or 21 - the field floors at 1, so there
                    // is no way to clear it first and 2 is unreachable. Found by
                    // Colby at Session 76, using it.
                    onFocus={e => e.target.select()}
                    aria-label={`Copies of ${item.name || 'item'}`}
                    style={{
                      width: 58, minHeight: 40, textAlign: 'center', fontSize: 16,
                      fontWeight: 700, color: '#1a1a1a', background: '#fff',
                      border: '1px solid #DDE1E6', borderRadius: 8,
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section label="Send to">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {destinationRows.map(row => (
              <DestinationRow
                key={row.id}
                row={row}
                selected={row.id === destinationId}
                accent={accent}
                onSelect={() => !row.blockedReason && setDestinationId(row.id)}
              />
            ))}
          </div>

          {destinationId === 'zebra' && zebraPrinters.length > 1 && (
            <select
              value={zebraPrinterId || ''}
              onChange={e => setZebraPrinterId(e.target.value)}
              style={{
                marginTop: 10, width: '100%', padding: '10px 12px', borderRadius: 8,
                border: '1px solid #DDE1E6', fontSize: 16, background: '#fff',
              }}
            >
              {zebraPrinters.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
        </Section>

        {/* Callout E: the roll maths, shown before committing, because vinyl is
            consumed. No CMMS competitor surfaces this. */}
        {canonLayout && canonLayout.across > 0 && (
          <div style={{
            background: '#F7F8F9', borderRadius: 10, padding: '12px 14px',
            display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 12.5, color: '#4B5563',
          }}>
            <span><strong>{canonLayout.across}</strong> across</span>
            <span><strong>{canonLayout.rows}</strong> row{canonLayout.rows === 1 ? '' : 's'}</span>
            <span><strong>{canonLayout.lengthFt.toFixed(2)} ft</strong> of roll</span>
            <span><strong>{Math.round(canonLayout.fillPercent)}%</strong> width used</span>
          </div>
        )}

        {error && (
          <div style={{
            marginTop: 14, padding: '10px 12px', borderRadius: 8,
            background: '#FDECEC', color: '#B3261E', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        {done && (
          <div style={{
            marginTop: 14, padding: '10px 12px', borderRadius: 8,
            background: '#EAF7F1', color: '#1D6B4F', fontSize: 13,
          }}>
            {destination?.id === 'canon'
              ? 'PDF downloaded. Send it to production when you are ready.'
              : `${totalLabels} label${totalLabels === 1 ? '' : 's'} sent to ${destination?.label}.`}
          </div>
        )}

        {/* #113, Session 77. A marked tile in the PDF is only half a fix - the
            person who has to act on it is looking at this screen, not at page
            three of the export. Amber rather than red because the run
            succeeded: callout G's rule, that partial failure is the normal case
            and the reassurance matters as much as the warning. */}
        {done && result?.qrFailures > 0 && (
          <div style={{
            marginTop: 10, padding: '10px 12px', borderRadius: 8,
            background: '#FFF8E8', color: '#96650E', fontSize: 13,
          }}>
            <strong>{result.qrFailures} label{result.qrFailures === 1 ? '' : 's'}</strong>
            {' '}could not have a QR code generated and {result.qrFailures === 1 ? 'is' : 'are'} marked
            {' '}<strong>QR FAILED</strong> on the sheet. Discard {result.qrFailures === 1 ? 'it' : 'them'} before
            {' '}cutting — the other {totalLabels - result.qrFailures} {totalLabels - result.qrFailures === 1 ? 'is' : 'are'} fine.
          </div>
        )}
      </div>

      <ActionBar
        summary={[
          `${totalLabels} label${totalLabels === 1 ? '' : 's'}`,
          copiesVary ? 'counts vary by item' : `${copies} cop${copies === 1 ? 'y' : 'ies'} each`,
          estimate,
        ]}
        accent={accent}
        actionLabel={busy ? 'Working…' : actionLabel}
        disabled={busy || !destinationId || totalLabels < 1}
        onAction={onPrint}
        onClose={onClose}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screen 3 — a Zebra batch in flight
// ---------------------------------------------------------------------------

function LiveBatch({ counts, total, printerName, branchName, accent, onCancelRemaining, onRetryFailed, onClose }) {
  const stats = [
    { label: 'done', value: counts.done, color: '#1D9E75' },
    { label: 'sent', value: counts.sent, color: '#378ADD' },
    { label: 'queued', value: counts.queued, color: '#8B94A1' },
    { label: 'failed', value: counts.failed, color: '#E24B4A' },
  ];
  const unaffected = total - counts.failed;

  return (
    <div>
      <Header
        title={`Printing ${total} label${total === 1 ? '' : 's'}`}
        subtitle={[printerName, branchName].filter(Boolean).join(' · ')}
        onClose={onClose}
      />
      <div style={{ padding: '18px 22px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {stats.map(s => (
            <div key={s.label} style={{
              flex: '1 1 90px', background: '#F7F8F9', borderRadius: 10,
              padding: '12px 14px', textAlign: 'center',
            }}>
              <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11.5, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Callout G: name the cause and the remedy, then reassure. The last
            clause matters most — partial failure is the normal case in batch
            printing, and the instinctive fear is that the whole run is ruined. */}
        {counts.failed > 0 && (
          <div style={{
            marginTop: 14, padding: '12px 14px', borderRadius: 8,
            background: '#FDECEC', color: '#B3261E', fontSize: 13, lineHeight: 1.5,
          }}>
            <strong>{counts.failed} label{counts.failed === 1 ? '' : 's'} failed.</strong>{' '}
            The printer reported it was out of media. Load labels and retry — the other {unaffected} are unaffected.
          </div>
        )}
      </div>

      {/* Callout H: cancel and retry are separate actions with different scopes.
          Neither is "start over" — that would reprint labels already on shelves. */}
      <div style={{
        display: 'flex', gap: 10, padding: '14px 22px',
        borderTop: '1px solid #EDEFF2', justifyContent: 'flex-end',
      }}>
        <button onClick={onCancelRemaining} disabled={counts.queued < 1} style={secondaryButton(counts.queued < 1)}>
          Cancel remaining
        </button>
        <button onClick={onRetryFailed} disabled={counts.failed < 1} style={primaryButton(accent, counts.failed < 1)}>
          Retry {counts.failed} failed
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Header({ title, subtitle, onClose }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      padding: '18px 22px 14px', borderBottom: '1px solid #EDEFF2',
    }}>
      <div>
        <div style={{ fontFamily: 'var(--font-head, inherit)', fontSize: 19, fontWeight: 700, color: '#1a1a1a' }}>
          {title}
        </div>
        <div style={{ fontSize: 12.5, color: '#8B94A1', marginTop: 3 }}>{subtitle}</div>
      </div>
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          background: 'none', border: 'none', fontSize: 20, lineHeight: 1,
          color: '#8B94A1', cursor: 'pointer', padding: 4, marginTop: -2,
        }}
      >
        ✕
      </button>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
        color: '#8B94A1', marginBottom: 10,
      }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function SizeChip({ format, selected, accent, compact, onSelect }) {
  return (
    <button
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        flex: compact ? '0 0 auto' : '0 0 auto',
        textAlign: 'left',
        padding: compact ? '8px 12px' : '9px 13px',
        borderRadius: 9,
        border: `1.5px solid ${selected ? accent : '#DDE1E6'}`,
        background: selected ? `${accent}12` : '#fff',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, color: selected ? accent : '#1a1a1a' }}>
        {format.label}
      </div>
      <div style={{ fontSize: 10.5, color: '#8B94A1', marginTop: 1 }}>
        {compact ? format.scanText.replace('scans ', '') : format.scanText}
      </div>
    </button>
  );
}

function Stepper({ value, onChange, accent }) {
  const btn = {
    width: 40, height: 40, borderRadius: 9, border: '1px solid #DDE1E6',
    background: '#fff', fontSize: 20, lineHeight: 1, cursor: 'pointer', color: '#374151',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button onClick={() => onChange(Math.max(1, value - 1))} aria-label="Fewer copies" style={btn}>−</button>
      {/* 16px minimum — global rule, iOS auto-zoom. */}
      <div style={{
        minWidth: 54, textAlign: 'center', fontSize: 17, fontWeight: 700,
        color: accent, fontVariantNumeric: 'tabular-nums',
      }}>
        {value}
      </div>
      <button onClick={() => onChange(Math.min(99, value + 1))} aria-label="More copies" style={btn}>+</button>
    </div>
  );
}

function DestinationRow({ row, selected, accent, onSelect }) {
  const blocked = !!row.blockedReason;
  return (
    <button
      onClick={onSelect}
      disabled={blocked}
      aria-pressed={selected}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        width: '100%', textAlign: 'left', gap: 12,
        padding: '12px 14px', borderRadius: 10,
        border: `1.5px solid ${selected ? accent : '#DDE1E6'}`,
        background: blocked ? '#F7F8F9' : (selected ? `${accent}12` : '#fff'),
        cursor: blocked ? 'not-allowed' : 'pointer',
        opacity: blocked ? 0.65 : 1,
      }}
    >
      <span style={{ fontSize: 14, fontWeight: 600, color: blocked ? '#8B94A1' : '#1a1a1a' }}>
        {row.label}
      </span>
      {/* Callout C: greyed WITH a reason, never hidden. "No printer at this
          branch" is a fact a manager can act on. */}
      <span style={{ fontSize: 12, color: blocked ? '#8B94A1' : (selected ? accent : '#6B7280'), textAlign: 'right' }}>
        {row.blockedReason || row.verb}
      </span>
    </button>
  );
}

function ActionBar({ summary, accent, actionLabel, disabled, onAction, onClose }) {
  return (
    <div style={{ borderTop: '1px solid #EDEFF2', padding: '12px 22px 16px', background: '#fff' }}>
      <div style={{
        display: 'flex', gap: 14, flexWrap: 'wrap',
        fontSize: 12, color: '#8B94A1', marginBottom: 10,
      }}>
        {summary.filter(Boolean).map((s, i) => <span key={i}>{s}</span>)}
      </div>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={secondaryButton(false)}>Cancel</button>
        <button onClick={onAction} disabled={disabled} style={primaryButton(accent, disabled)}>
          {actionLabel}
        </button>
      </div>
    </div>
  );
}

function primaryButton(accent, disabled) {
  return {
    padding: '12px 18px', borderRadius: 9, border: 'none',
    background: disabled ? '#C9CED4' : accent, color: '#fff',
    fontSize: 15, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    minHeight: 44,
  };
}

function secondaryButton(disabled) {
  return {
    padding: '12px 18px', borderRadius: 9, border: '1px solid #DDE1E6',
    background: '#fff', color: disabled ? '#B6BCC4' : '#374151',
    fontSize: 15, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
    minHeight: 44,
  };
}
